import { supabase } from '../lib/supabase';
import type {
  Invoice,
  InvoiceWithPayments,
  InvoiceType,
  Payment,
} from '../types/multiInfluencer';

/** IVA rate applied on top of the net amounts users type. */
export const VAT_RATE = 0.23;

/**
 * Amounts entered in the app are net (without IVA); whoever invoices adds IVA
 * on top. Store the net as subtotal and the gross as total so the invoices
 * table is self-consistent and the Creators Hub tax board can show IVA.
 */
export function netToInvoiceAmounts(net: number): { subtotal: number; vat_rate: number; total: number } {
  const subtotal = Math.round((Number(net) || 0) * 100) / 100;
  return {
    subtotal,
    vat_rate: VAT_RATE,
    total: Math.round(subtotal * (1 + VAT_RATE) * 100) / 100,
  };
}

export const invoiceService = {
  /**
   * Get all invoices for a campaign
   */
  async getByCampaign(campaignId: string): Promise<InvoiceWithPayments[]> {
    const { data: invoices, error } = await supabase
      .from('invoices')
      .select('*')
      .eq('campaign_id', campaignId)
      .order('display_order', { ascending: true })
      .order('created_at', { ascending: false });

    if (error) throw error;

    // Enrich with payments
    const enriched = await Promise.all(
      (invoices || []).map(async (inv) => {
        const payments = await this.getPaymentsByInvoice(inv.id);
        const total_paid = payments.reduce((sum, p) => sum + p.amount, 0);
        const remaining = inv.total - total_paid;
        const days_overdue = inv.is_overdue && inv.due_date ?
          this.calculateDaysOverdue(inv.due_date) : null;
        const latest_payment_date = payments.length > 0 ? payments[0].paid_at : null;

        return {
          ...inv,
          payments,
          total_paid,
          remaining,
          days_overdue,
          latest_payment_date,
        };
      })
    );

    return enriched;
  },

  /**
   * Get client invoices for a campaign
   */
  async getClientInvoices(campaignId: string): Promise<InvoiceWithPayments[]> {
    const allInvoices = await this.getByCampaign(campaignId);
    return allInvoices.filter((inv) => inv.type === 'Client');
  },

  /**
   * Get influencer invoices for a campaign
   */
  async getInfluencerInvoices(campaignId: string): Promise<InvoiceWithPayments[]> {
    const allInvoices = await this.getByCampaign(campaignId);
    return allInvoices.filter((inv) => inv.type === 'Influencer');
  },

  /**
   * Get invoices for a specific campaign influencer
   */
  async getByInfluencer(campaignInfluencerId: string): Promise<InvoiceWithPayments[]> {
    const { data: invoices, error } = await supabase
      .from('invoices')
      .select('*')
      .eq('campaign_influencer_id', campaignInfluencerId)
      .order('issue_date', { ascending: false, nullsFirst: false });

    if (error) throw error;

    // Enrich with payments
    const enriched = await Promise.all(
      (invoices || []).map(async (inv) => {
        const payments = await this.getPaymentsByInvoice(inv.id);
        const total_paid = payments.reduce((sum, p) => sum + p.amount, 0);
        const remaining = inv.total - total_paid;
        const days_overdue = inv.is_overdue && inv.due_date ?
          this.calculateDaysOverdue(inv.due_date) : null;
        const latest_payment_date = payments.length > 0 ? payments[0].paid_at : null;

        return {
          ...inv,
          payments,
          total_paid,
          remaining,
          days_overdue,
          latest_payment_date,
        };
      })
    );

    return enriched;
  },

  /**
   * Create a new invoice
   */
  async create(
    data: Omit<Invoice, 'id' | 'created_at' | 'updated_at' | 'is_overdue' | 'display_order'> & { display_order?: number },
    payment_date?: string
  ) {
    const { data: created, error } = await supabase
      .from('invoices')
      .insert(data)
      .select()
      .maybeSingle();

    if (error) throw error;
    if (!created) throw new Error('Failed to create invoice');

    // If invoice status is Paid and payment_date is provided, create payment record
    if (data.status === 'Paid' && payment_date && created.total > 0) {
      await this.recordPayment({
        invoice_id: created.id,
        amount: created.total,
        paid_at: payment_date,
        method: 'Bank Transfer',
        reference: null,
        notes: 'Payment recorded during invoice creation',
      });
    }

    return created;
  },

  /**
   * Update invoice
   */
  async update(id: string, data: Partial<Invoice>) {
    const { data: updated, error } = await supabase
      .from('invoices')
      .update(data)
      .eq('id', id)
      .select()
      .maybeSingle();

    if (error) throw error;
    if (!updated) throw new Error('Invoice not found or update failed');
    return updated;
  },

  /**
   * Delete invoice (only if no payments exist)
   */
  async delete(id: string) {
    // Check for payments first
    const { data: payments } = await supabase
      .from('payments')
      .select('id')
      .eq('invoice_id', id);

    if (payments && payments.length > 0) {
      throw new Error('Cannot delete invoice with payments. Cancel it instead.');
    }

    const { error } = await supabase
      .from('invoices')
      .delete()
      .eq('id', id);

    if (error) throw error;
  },

  /**
   * Cancel invoice
   */
  async cancel(id: string) {
    return this.update(id, { status: 'Canceled' });
  },

  /**
   * Get single invoice by ID
   */
  async getById(id: string): Promise<Invoice | null> {
    const { data, error } = await supabase
      .from('invoices')
      .select('*')
      .eq('id', id)
      .maybeSingle();

    if (error) throw error;
    return data;
  },

  /**
   * Get payments for an invoice
   */
  async getPaymentsByInvoice(invoiceId: string): Promise<Payment[]> {
    const { data, error } = await supabase
      .from('payments')
      .select('*')
      .eq('invoice_id', invoiceId)
      .order('paid_at', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  /**
   * Record a payment for an invoice
   */
  async recordPayment(payment: Omit<Payment, 'id' | 'created_at' | 'updated_at'>) {
    const { data, error } = await supabase
      .from('payments')
      .insert(payment)
      .select()
      .single();

    if (error) throw error;

    // Status is automatically updated by trigger
    return data;
  },

  /**
   * Keep the payment record in sync with the invoice's status/date fields.
   * Forms store a single payment date per invoice; the payments table is the
   * source of truth on reload, so it must be written on every save.
   */
  async syncPaymentRecord(invoiceId: string, status: string, total: number, paidAt: string | null | undefined) {
    const payments = await this.getPaymentsByInvoice(invoiceId);

    if (status === 'Paid' && paidAt) {
      if (payments.length === 0) {
        await this.recordPayment({
          invoice_id: invoiceId,
          amount: total,
          paid_at: paidAt,
          method: 'Bank Transfer',
          reference: null,
          notes: 'Payment recorded from campaign form',
        });
      } else {
        const latest = payments[0];
        // paid_at may come back as a timestamp; compare date parts only
        if ((latest.paid_at || '').slice(0, 10) !== paidAt.slice(0, 10) || latest.amount !== total) {
          await this.updatePayment(latest.id, { paid_at: paidAt, amount: total });
        }
      }
    } else if (status !== 'Paid' && payments.length > 0) {
      for (const p of payments) {
        await this.deletePayment(p.id);
      }
    }
  },

  /**
   * Update payment
   */
  async updatePayment(id: string, data: Partial<Payment>) {
    const { data: updated, error } = await supabase
      .from('payments')
      .update(data)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return updated;
  },

  /**
   * Delete payment
   */
  async deletePayment(id: string) {
    const { error } = await supabase
      .from('payments')
      .delete()
      .eq('id', id);

    if (error) throw error;
    // Invoice status is automatically updated by trigger
  },

  /**
   * Get overdue invoices
   */
  async getOverdue(): Promise<InvoiceWithPayments[]> {
    const { data: invoices, error } = await supabase
      .from('invoices')
      .select(`
        *,
        campaigns!inner(work_code, client)
      `)
      .eq('is_overdue', true)
      .order('due_date', { ascending: true });

    if (error) throw error;

    // Enrich with payments
    const enriched = await Promise.all(
      (invoices || []).map(async (inv) => {
        const payments = await this.getPaymentsByInvoice(inv.id);
        const total_paid = payments.reduce((sum, p) => sum + p.amount, 0);
        const remaining = inv.total - total_paid;
        const days_overdue = inv.due_date ? this.calculateDaysOverdue(inv.due_date) : null;

        return {
          ...inv,
          payments,
          total_paid,
          remaining,
          days_overdue,
        };
      })
    );

    return enriched;
  },

  /**
   * Calculate days overdue
   */
  calculateDaysOverdue(dueDate: string): number {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = new Date(dueDate);
    due.setHours(0, 0, 0, 0);
    const diffTime = today.getTime() - due.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  },

  /**
   * Mark invoices as overdue
   */
  async markOverdue() {
    const { error } = await supabase.rpc('mark_overdue_items');
    if (error) throw error;
  },

  /**
   * Calculate invoice total from subtotal and VAT
   */
  calculateTotal(subtotal: number, vatRate: number): number {
    return subtotal * (1 + vatRate);
  },

  /**
   * Get invoices not yet issued for campaigns with all deliverables published
   */
  async getNotIssued(): Promise<InvoiceWithPayments[]> {
    // This would need a more complex query or client-side filtering
    // For now, get all Draft invoices
    const { data: invoices, error } = await supabase
      .from('invoices')
      .select(`
        *,
        campaigns!inner(work_code, client)
      `)
      .eq('status', 'Draft')
      .order('created_at', { ascending: true });

    if (error) throw error;

    const enriched = await Promise.all(
      (invoices || []).map(async (inv) => ({
        ...inv,
        payments: [],
        total_paid: 0,
        remaining: inv.total,
        days_overdue: null,
      }))
    );

    return enriched;
  },

  /**
   * Update display order for multiple invoices
   */
  async updateDisplayOrder(invoiceOrders: { id: string; display_order: number }[]): Promise<void> {
    // Update all invoices in parallel
    const updates = invoiceOrders.map(({ id, display_order }) =>
      supabase
        .from('invoices')
        .update({ display_order })
        .eq('id', id)
    );

    const results = await Promise.all(updates);

    // Check for errors
    const errors = results.filter(r => r.error);
    if (errors.length > 0) {
      throw new Error(`Failed to update invoice order: ${errors[0].error?.message}`);
    }
  },

  /**
   * Reorder invoices after drag and drop
   */
  async reorderInvoices(campaignId: string, type: InvoiceType, reorderedIds: string[]): Promise<void> {
    // Create order mapping
    const orderUpdates = reorderedIds.map((id, index) => ({
      id,
      display_order: index,
    }));

    await this.updateDisplayOrder(orderUpdates);
  },

  /**
   * Get commission breakdown per influencer for a client invoice.
   * Commission is proportionally split based on the invoice's share
   * of the total amount invoiced across all client invoices in the campaign.
   */
  async getInvoiceCommissionBreakdown(invoiceId: string, campaignId: string): Promise<InvoiceCommissionBreakdown> {
    const [influencersRes, allClientInvoicesRes, targetInvoiceRes] = await Promise.all([
      supabase
        .from('campaign_influencers')
        .select('id, influencer_name, agreed_fee, commission_pct, commission_type, commission_flat_fee')
        .eq('campaign_id', campaignId),
      supabase
        .from('invoices')
        .select('id, total')
        .eq('campaign_id', campaignId)
        .eq('type', 'Client'),
      supabase
        .from('invoices')
        .select('id, total')
        .eq('id', invoiceId)
        .maybeSingle(),
    ]);

    if (influencersRes.error) throw influencersRes.error;
    if (allClientInvoicesRes.error) throw allClientInvoicesRes.error;
    if (targetInvoiceRes.error) throw targetInvoiceRes.error;

    const influencers = influencersRes.data || [];
    const allClientInvoices = allClientInvoicesRes.data || [];
    const targetInvoice = targetInvoiceRes.data;

    if (!targetInvoice || influencers.length === 0) {
      return { items: [], totalCommission: 0, invoiceTotal: targetInvoice?.total ?? 0, splitRatio: 0 };
    }

    const totalInvoiced = allClientInvoices.reduce((s, inv) => s + Number(inv.total || 0), 0);
    const invoiceTotal = Number(targetInvoice.total || 0);
    const splitRatio = totalInvoiced > 0 ? invoiceTotal / totalInvoiced : 0;

    const items: InvoiceCommissionItem[] = influencers.map((inf) => {
      const agreedFee = Number(inf.agreed_fee || 0);
      const totalCommission = inf.commission_type === 'flat_fee'
        ? Number(inf.commission_flat_fee || 0)
        : agreedFee * Number(inf.commission_pct || 0);
      const commissionInInvoice = Math.round(totalCommission * splitRatio * 100) / 100;
      const feeInInvoice = Math.round(agreedFee * splitRatio * 100) / 100;

      return {
        influencerName: inf.influencer_name,
        agreedFee,
        feeInInvoice,
        totalCommission,
        commissionInInvoice,
        commissionType: inf.commission_type || 'percentage',
        commissionPct: Number(inf.commission_pct || 0),
      };
    });

    const totalCommission = items.reduce((s, i) => s + i.commissionInInvoice, 0);

    return { items, totalCommission, invoiceTotal, splitRatio };
  },
};

export interface InvoiceCommissionItem {
  influencerName: string;
  agreedFee: number;
  feeInInvoice: number;
  totalCommission: number;
  commissionInInvoice: number;
  commissionType: string;
  commissionPct: number;
}

export interface InvoiceCommissionBreakdown {
  items: InvoiceCommissionItem[];
  totalCommission: number;
  invoiceTotal: number;
  splitRatio: number;
}
